import { formatNumber } from '@angular/common';
import {
    afterNextRender,
    AfterViewInit,
    booleanAttribute,
    computed,
    Directive,
    inject,
    input,
    signal,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
    ApiTypes,
    DataLayerValue,
    isHWS,
    QuickfilterEntry,
    TrackingFeature,
    VehicleFeature,
} from '@hyundai/ng-common-lib';
import { Store } from '@ngrx/store';
import { DialogService } from '@pixelgenau/ngx-dialog';
import { firstValueFrom, switchMap } from 'rxjs';
import { FilterTag } from '../filter/filter.tag';

@Directive()
export default class QuickSearchBase implements AfterViewInit {
    dialog = inject(DialogService);
    store = inject(Store);
    activatedRoute = inject(ActivatedRoute);
    router = inject(Router);

    isHWS = isHWS();

    listLink = input.required<string>();
    showQuickFilter = input(true, { transform: booleanAttribute });

    constructor() {
        this.store.dispatch(VehicleFeature.vehicleActions.loadQuickSearch());

        afterNextRender({
            write: () => {
                this.store.dispatch(VehicleFeature.vehicleActions.loadFilter());
            },
        });
    }

    numberOfVehicles = this.store.selectSignal(VehicleFeature.vehicleState.selectNumberOfVehicles);
    previewNumberOfVehicles = this.store.selectSignal(VehicleFeature.vehicleState.selectFilterPreviewNumberOfVehicles);
    inputs = this.store.selectSignal(VehicleFeature.vehicleState.selectFilterInputs);
    selectedFilter = this.store.selectSignal(VehicleFeature.vehicleState.selectFilterSelectedPreview);
    loadingPreviewNumberOfVehicles = this.store.selectSignal(
        VehicleFeature.vehicleState.selectFilterLoadingPreviewNumberOfVehicle,
    );

    staticFilterHistorys = signal<QuickfilterEntry[] | null>(null);

    models = computed(() => {
        const inputs = this.inputs();
        if (!inputs) {
            return [];
        }
        return inputs.models.values.map((m) => ({ value: m, label: m }));
    });

    price = computed(() => {
        const inputs = this.inputs();
        if (!inputs) {
            return [];
        }
        return inputs.price.values.map((reg) => ({
            value: reg.toString(),
            label: `${formatNumber(reg, 'de-DE')} EUR`,
        }));
    });

    selectedCondition = computed(() => {
        const filter = this.selectedFilter();
        return filter.condition as string[] | undefined;
    });

    selectedHotDeal = computed(() => {
        const filter = this.selectedFilter();
        return !!filter.hotDeal;
    });

    milage = computed(() => {
        const inputs = this.inputs();
        if (!inputs) {
            return [];
        }

        const mileageFilter = inputs.mileage;

        if (mileageFilter.values.length === 0) {
            return [];
        }

        return mileageFilter.values.map((reg) => ({
            value: reg.toString(),
            label: `${formatNumber(reg, 'de-DE')} km`,
        }));
    });

    filterParam = computed(() => {
        const filter = this.selectedFilter();
        return Object.keys(filter).length > 0 ? JSON.stringify(filter) : undefined;
    });

    vehicleCount = computed(() => {
        const preview = this.previewNumberOfVehicles();
        const final = this.numberOfVehicles();
        if (preview !== null && preview !== false) {
            return preview;
        }
        if (final !== null && final !== false) {
            return final;
        }
        return 0;
    });

    // History als snapshot, damit sich die Historie nicht ändert, wenn man navigiert
    ngAfterViewInit(): void {
        const historys = this.store.selectSignal(VehicleFeature.vehicleState.selectFilterHistorys);
        this.staticFilterHistorys.set(historys());
    }

    async openFilterDialog() {
        const result = await firstValueFrom(this.dialog.openDialog(FilterTag).pipe(switchMap((d) => d.close$)));

        if (!result?.filter) {
            return;
        }

        const filter = this.selectedFilter();
        this.router.navigate([this.listLink()], {
            relativeTo: this.activatedRoute,
            queryParams: {
                filter: Object.keys(filter).length > 0 ? JSON.stringify(filter) : undefined,
                page: 1,
            },
            replaceUrl: true,
            queryParamsHandling: 'merge',
        });
    }

    addFilter(key: keyof ApiTypes.FilterObject, data: unknown, historyMessage: string) {
        this.store.dispatch(
            VehicleFeature.vehicleActions.addFilter({
                filterObject: {
                    [key]: data,
                },
                trackingKeys: [key],
                historyMessage: {
                    [key]: historyMessage,
                },
            }),
        );
    }

    removeFilter(key: keyof ApiTypes.FilterObject) {
        this.store.dispatch(
            VehicleFeature.vehicleActions.removeFilter({
                removeFilterObject: [key],
                trackingKeys: [key],
            }),
        );
    }

    removeAllFilters() {
        const filter = ['condition', 'hotDeal'] as Array<keyof ApiTypes.FilterObject>;
        this.store.dispatch(
            VehicleFeature.vehicleActions.removeFilter({
                removeFilterObject: filter,
                trackingKeys: filter,
            }),
        );
    }

    toggleFilter(
        key: keyof ApiTypes.FilterObject,
        data: unknown,
        historyMessage: string,
        removeFilter: keyof ApiTypes.FilterObject,
    ): void {
        this.store.dispatch(
            VehicleFeature.vehicleActions.addFilterAndRemoveFilter({
                filterObject: {
                    [key]: data,
                },
                trackingKeys: [key],
                historyMessage: {
                    [key]: historyMessage,
                },
                removeFilter: {
                    [removeFilter]: null,
                },
            }),
        );
    }
    submitFilter() {
        this.store.dispatch(VehicleFeature.vehicleActions.filter());
    }

    trackAdvancedFilter() {
        this.store.dispatch(
            TrackingFeature.actions.eventTrackingPush({
                dataLayer: {
                    event: 'filter_adjust',
                    page: {
                        overlay: 'filter',
                    },
                },
            }),
        );
    }

    trackSubmitFilter() {
        this.submitFilter();
        const filter = this.selectedFilter() as unknown as DataLayerValue;
        this.store.dispatch(
            TrackingFeature.actions.eventTrackingPush({
                dataLayer: {
                    event: 'filter_result',
                    filter,
                },
            }),
        );
    }

    trackQuickfilter() {
        this.store.dispatch(
            TrackingFeature.actions.eventTrackingPush({
                dataLayer: {
                    event: 'element_click',
                    page: {
                        element: {
                            type: 'button',
                            target: 'quick_filter',
                            label: 'filter_shortcut',
                        },
                    },
                },
            }),
        );
    }

    addQuickfilter() {
        const filter = this.selectedFilter();
        if (this.vehicleCount() === 0 || Object.keys(filter).length === 0) {
            return;
        }

        this.store.dispatch(VehicleFeature.vehicleActions.setHistory({ history: filter }));
    }
}
